import { AirtableRecord } from '@/modules/core/data/types/AirtableRecord';
import { Campus } from '@/modules/core/data/types/Campus';
import ChatbotQuestion from '@/modules/core/data/types/CDE/ChatbotQuestion';
import { ChatbotContext } from '@/modules/core/data/types/ChatbotContext';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { ModChatbot } from '@/modules/core/data/types/ModChatbot';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import find from 'lodash.find';
import size from 'lodash.size';
import {
  Dispatch,
  SetStateAction,
} from 'react';
import { filterStepsByPrograms } from '../../solutions/educationalProgramStep';
import { ChatbotQuestions } from './getQuestionsToAsk';

/**
 * Finds the next question to ask based on the question that's been answered and the next dialog context to come.
 *
 * Ignores questions that don't have any choices (no possible answer).
 */
export const findNextQuestionToAsk = (currentQuestion: ChatbotQuestion, nextContext: ChatbotContext, questions: ChatbotQuestions, allEducationalPrograms: EducationalProgram[], allEducationalProgramSteps: EducationalProgramStep[], allCampuses: Campus[], allStudentSolutionsCategories: StudentSolutionsCategory[], modChatbot: AirtableRecord<ModChatbot>, setContext: Dispatch<SetStateAction<ChatbotContext>>): ChatbotQuestion => {
  const {
    personaQuestion,
    programsQuestion,
    programStepsQuestion,
    campusesQuestion,
    categoryQuestion,
  } = questions;
  const hasSelectedPersona = typeof nextContext.persona === 'string' && size(nextContext?.persona) >= 1;
  const hasSelectedProgram = size(nextContext?.educationalPrograms) >= 1;
  const hasSelectedProgramStep = size(nextContext?.educationalProgramSteps) >= 1;
  const hasSelectedCampus = size(nextContext?.campuses) >= 1;
  const hasSelectedCategory = size(nextContext?.categories) >= 1;
  const isCategoryQuestionEnabled = modChatbot?.isCategoryQuestionEnabled;

  const isThereAnyStepChoice = !!size(allEducationalProgramSteps);
  const isThereOnlyOneEducationProgramChoice = size(allEducationalPrograms) === 1;
  const onlyAvailableProgram = isThereOnlyOneEducationProgramChoice ? allEducationalPrograms[0] : null;
  const availableStepsForTheOnlyAvailableProgram = isThereOnlyOneEducationProgramChoice ? filterStepsByPrograms(onlyAvailableProgram?.steps, allEducationalPrograms) : null;
  const isThereMultipleAvailableStepsForOnlyAvailableProgram = size(availableStepsForTheOnlyAvailableProgram) > 1;
  const isThereOnlyOneAvailableStepForOnlyAvailableProgram = size(availableStepsForTheOnlyAvailableProgram) === 1;
  const hasNoAvailableStepForTheOnlyAvailableProgram = size(availableStepsForTheOnlyAvailableProgram) === 0;
  const onlyAvailableStepForTheOnlyAvailableProgram = onlyAvailableProgram ? onlyAvailableProgram?.steps?.[0] : null;
  const onlySelectedProgram = find(allEducationalPrograms, { ref: nextContext?.educationalPrograms?.[0]?.ref });
  const availableStepsForOnlySelectedProgram = onlySelectedProgram?.steps;
  const isThereAnyCampusChoice = !!size(allCampuses);
  const isThereAnyCategoryChoice = !!size(allStudentSolutionsCategories);

  // Handle top-level logic (don't dive too much inside the programs/programSteps because they have their own logic (which is quite complex)
  if (hasSelectedPersona) {
    if (hasSelectedProgram) {
      if (hasSelectedProgramStep) {
        if (hasSelectedCampus) {
          if (hasSelectedCategory) {
            setContext(nextContext);
            return currentQuestion; // Stay on the same question because there are no more question to ask for
          } else {
            if (isCategoryQuestionEnabled && isThereAnyCategoryChoice) {
              setContext(nextContext);
              return categoryQuestion;
            } else {
              setContext(nextContext);
              return currentQuestion; // Stay on the same question because there are no more question to ask for
            }
          }
        } else {
          if (isThereAnyCampusChoice) {
            setContext(nextContext);
            return campusesQuestion;
          } else {
            if (isCategoryQuestionEnabled && isThereAnyCategoryChoice) {
              setContext(nextContext);
              return categoryQuestion;// Move on to the category question
            } else {
              setContext(nextContext);
              return currentQuestion; // Stay on the same question because there are no more question to ask for
            }
          }
        }
      }
    }

    // If there aren't any programs steps then we avoid the program/step questions and go directly to the next question
    if (isThereAnyStepChoice) {
      // If there is exactly one available program then we'll auto select program/steps depending on what choices the user has
      if (isThereOnlyOneEducationProgramChoice) {
        if (isThereMultipleAvailableStepsForOnlyAvailableProgram) {
          // If the only available program has several steps, then we automatically select the program and display the step question
          nextContext.educationalPrograms = [onlyAvailableProgram];
          setContext(nextContext);

          return programStepsQuestion;
        } else if (isThereOnlyOneAvailableStepForOnlyAvailableProgram) {
          // If the only available program has only one available step, then we automatically select both program and step and display the campus question
          nextContext.educationalPrograms = [onlyAvailableProgram];
          nextContext.educationalProgramSteps = [onlyAvailableStepForTheOnlyAvailableProgram];
          setContext(nextContext);

          if (isThereAnyCampusChoice) {
            return campusesQuestion;
          } else {
            if (isCategoryQuestionEnabled && isThereAnyCategoryChoice) {
              setContext(nextContext);
              return categoryQuestion;
            } else {
              setContext(nextContext);
              return currentQuestion; // Stay on the same question because there are no more question to ask for
            }
          }

        } else if (hasNoAvailableStepForTheOnlyAvailableProgram) {
          // If the only available program has no available step, then we select the program and display the campus question
          nextContext.educationalPrograms = [onlyAvailableProgram];
          setContext(nextContext);

          if (isThereAnyCampusChoice) {
            return campusesQuestion;
          } else {
            if (isCategoryQuestionEnabled && isThereAnyCategoryChoice) {
              return categoryQuestion;
            } else {
              return currentQuestion; // Stay on the same question because there are no more question to ask for
            }
          }
        }
      } else {
        // If the program is already selected, then select the step
        if (hasSelectedProgram) {
          if (size(availableStepsForOnlySelectedProgram) === 1) {
            // Auto-select the step if there is only one available
            nextContext.educationalProgramSteps = [availableStepsForOnlySelectedProgram[0]];
            setContext(nextContext);

            if (isThereAnyCampusChoice) {
              return campusesQuestion;
            } else {
              if (isCategoryQuestionEnabled && isThereAnyCategoryChoice) {
                return categoryQuestion;
              } else {
                return currentQuestion; // Stay on the same question because there are no more question to ask for
              }
            }
          } else {
            setContext(nextContext);
            return programStepsQuestion;
          }
        } else {
          // If there are many programs, then we ask the program question
          setContext(nextContext);
          return programsQuestion;
        }
      }
    } else {
      // If there is no choice for steps, then no need to ask either steps/program questions
      setContext(nextContext);

      if (isThereAnyCampusChoice) {
        if (hasSelectedCampus) {
          if (isCategoryQuestionEnabled && isThereAnyCategoryChoice) {
            return categoryQuestion;
          } else {
            return currentQuestion; // Stay on the same question because there are no more question to ask for
          }
        } else {
          return campusesQuestion;
        }
      } else {
        if (isCategoryQuestionEnabled && isThereAnyCategoryChoice) {
          return categoryQuestion;
        } else {
          return currentQuestion; // Stay on the same question because there are no more question to ask for
        }
      }
    }
  } else {
    // Ask persona until it's set
    setContext(nextContext);
    return personaQuestion;
  }
};

export default findNextQuestionToAsk;
